import { computeIsSelfImprovementAvailable } from "@app/lib/client/self_improvement";
import type { AppType } from "@app/types/app";
import type {
  ConcreteResourceType,
  GrantVerb,
} from "@app/types/group_permissions";
import { isCreditPricedPlan, type SubscriptionType } from "@app/types/plan";
import {
  isComputerFeatureEnabled,
  type WhitelistableFeature,
} from "@app/types/shared/feature_flags";
import type { WorkspaceType } from "@app/types/user";
import { isAdmin, isManager } from "@app/types/user";
import {
  BarChart01,
  BookOpen01,
  Brackets,
  Brain,
  ClockRefresh,
  CloudArrowLeftRight,
  CreditCard01,
  File04,
  FolderOpen,
  Globe01,
  Lock01,
  PieChart01,
  PuzzlePiece01,
  Robot,
  Settings01,
  Shapes,
  ShapesPlus,
  Stars02,
  Terminal,
  Toggle01Left,
  User01,
  Users01,
  Zap,
} from "@dust-tt/sparkle";

/**
 * Check if an actual route path matches any of the given route patterns.
 * Supports both Next.js patterns like "/w/[wId]/members" and actual paths like "/w/abc123/members".
 * @param currentRoute - The actual route path (e.g., "/w/abc123/members")
 * @param patterns - Array of route patterns to match against
 */
function matchesRoutePattern(
  currentRoute: string,
  patterns: string[]
): boolean {
  // First try exact match (works for Next.js where pathname is the pattern)
  if (patterns.includes(currentRoute)) {
    return true;
  }

  // Convert patterns to regexes and try matching (works for SPA where pathname is actual path)
  return patterns.some((pattern) => {
    // Escape special regex chars except [ and ]
    const escaped = pattern.replace(/[.*+?^${}()|\\]/g, "\\$&");
    // Convert [paramName] to [^/]+ to match any segment
    const regexStr = "^" + escaped.replace(/\[[^\]]+\]/g, "[^/]+") + "$";
    return new RegExp(regexStr).test(currentRoute);
  });
}

/**
 * NavigationIds are typed ids we use to identify which navigation item is currently active. We need
 * ones for the topNavigation (same across the whole app) and for the subNavigation which appears in
 * some section of the app in the AppLayout navigation panel.
 */
type TopNavigationId =
  | "conversations"
  | "assistants"
  | "admin"
  | "data_sources";

type SubNavigationConversationsId = "conversation" | "personal_assistants";

type SubNavigationAssistantsId =
  | "data_sources_managed"
  | "data_sources_static"
  | "workspace_assistants"
  | "personal_assistants"
  | "data_sources_url"
  | "developers"
  | "documentation"
  | "community"
  | "spaces";

type SubNavigationAdminId =
  | "personal_settings"
  | "my_wiki"
  | "routines"
  | "subscription"
  | "billing"
  | "governance"
  | "workspace_branding"
  | "model_providers"
  | "members"
  | "identity_and_provisioning"
  | "providers"
  | "api_keys"
  | "dev_secrets"
  | "sandbox"
  | "analytics"
  | "credits_usage"
  | "usage"
  | "self_improving_skills"
  | "spaces_connections"
  | "spaces_tools"
  | "spaces_triggers"
  | "agents"
  | "skills";

const ADMIN_ROUTE_PATTERNS: Record<SubNavigationAdminId, string[]> = {
  personal_settings: ["/w/[wId]/me"],
  my_wiki: ["/w/[wId]/me/wiki"],
  routines: ["/w/[wId]/routines", "/w/[wId]/routines/[routineId]"],
  members: ["/w/[wId]/members"],
  identity_and_provisioning: ["/w/[wId]/identity-and-provisioning"],
  governance: ["/w/[wId]/governance"],
  workspace_branding: ["/w/[wId]/brand"],
  model_providers: ["/w/[wId]/model-providers"],
  analytics: ["/w/[wId]/analytics"],
  subscription: ["/w/[wId]/subscription"],
  billing: ["/w/[wId]/billing"],
  api_keys: ["/w/[wId]/developers/api-keys"],
  credits_usage: ["/w/[wId]/developers/credits-usage"],
  providers: ["/w/[wId]/developers/providers"],
  dev_secrets: ["/w/[wId]/developers/dev-secrets"],
  sandbox: ["/w/[wId]/developers/sandbox"],
  usage: ["/w/[wId]/usage"],
  self_improving_skills: ["/w/[wId]/developers/self-improving-skills"],
  agents: ["/w/[wId]/builder/agents"],
  skills: ["/w/[wId]/builder/skills"],
  spaces_connections: [],
  spaces_tools: [],
  spaces_triggers: [],
};

export type SubNavigationAppId =
  | "specification"
  | "datasets"
  | "execute"
  | "runs"
  | "settings";

export type AppLayoutNavigation = {
  id:
    | TopNavigationId
    | SubNavigationConversationsId
    | SubNavigationAssistantsId
    | SubNavigationAdminId
    | SubNavigationAppId;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  href?: string;
  target?: string;
  sizing?: "hug" | "expand";
  hasSeparator?: boolean;
  current: boolean;
  featureFlag?: WhitelistableFeature;
  // When true, the item is shown but greyed out and not navigable (the current
  // role lacks the permission to access it).
  disabled?: boolean;
};

type TabAppLayoutNavigation = {
  id:
    | TopNavigationId
    | SubNavigationConversationsId
    | SubNavigationAssistantsId
    | SubNavigationAdminId
    | SubNavigationAppId;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  href?: string;
  sizing?: "hug" | "expand";
  hasSeparator?: boolean;
  current?: never;
  isCurrent: (currentRoute: string) => boolean;
  ref?: React.RefObject<HTMLDivElement>;
};

export type SidebarNavigation = {
  id:
    | "assistants"
    | "data_sources"
    | "workspace"
    | "developers"
    | "help"
    | "api"
    | "spaces";
  label: string | null;
  menus: AppLayoutNavigation[];
};

/**
 * True when the current route belongs in the settings modal (admin settings,
 * personal settings, and spaces).
 */
export function isAdminNavigationRoute(currentRoute: string): boolean {
  return (
    matchesRoutePattern(currentRoute, [
      "/w/[wId]/me",
      "/w/[wId]/me/wiki",
      "/w/[wId]/members",
      "/w/[wId]/identity-and-provisioning",
      "/w/[wId]/governance",
      "/w/[wId]/branding",
      "/w/[wId]/model-providers",
      "/w/[wId]/subscription",
      "/w/[wId]/billing",
      "/w/[wId]/analytics",
      "/w/[wId]/actions",
      "/w/[wId]/developers/credits-usage",
      "/w/[wId]/developers/providers",
      "/w/[wId]/developers/api-keys",
      "/w/[wId]/developers/dev-secrets",
      "/w/[wId]/developers/sandbox",
      "/w/[wId]/usage",
      "/w/[wId]/developers/self-improving-skills",
      "/w/[wId]/builder/agents",
      "/w/[wId]/builder/skills",
      ...ADMIN_ROUTE_PATTERNS.routines,
    ]) ||
    currentRoute.startsWith("/w/[wId]/spaces") ||
    /^\/w\/[^/]+\/spaces/.test(currentRoute)
  );
}

export const subNavigationAdmin = ({
  owner,
  currentRoute,
  featureFlags,
  subscription,
  hasPermission,
  systemSpaceId,
  asPath,
}: {
  owner: WorkspaceType;
  currentRoute: string;
  featureFlags: WhitelistableFeature[];
  subscription: SubscriptionType;
  hasPermission: (
    verb: GrantVerb,
    resourceType: ConcreteResourceType
  ) => boolean;
  systemSpaceId?: string | null;
  asPath?: string;
}): SidebarNavigation[] => {
  const nav: SidebarNavigation[] = [];

  const canAdminBilling = hasPermission("admin", "billing");
  const canAdminSecurity = hasPermission("admin", "security");

  // Admins and managers see the full admin sidebar. Members still get Personal
  // Settings so `/me` has a left-nav entry without other admin items.
  if (!isManager(owner) && !canAdminBilling && !canAdminSecurity) {
    if (subscription.plan.limits.canUseProduct) {
      nav.push({
        id: "workspace",
        label: "Account",
        menus: [
          {
            id: "personal_settings",
            label: "Personal Settings",
            icon: User01,
            href: `/w/${owner.sId}/me`,
            current: matchesRoutePattern(
              currentRoute,
              ADMIN_ROUTE_PATTERNS.personal_settings
            ),
          },
          {
            id: "my_wiki",
            label: "My Wiki",
            icon: BookOpen01,
            href: `/w/${owner.sId}/me/wiki`,
            current: matchesRoutePattern(
              currentRoute,
              ADMIN_ROUTE_PATTERNS.my_wiki
            ),
          },
          {
            id: "routines",
            label: "Routines",
            icon: ClockRefresh,
            href: `/w/${owner.sId}/routines`,
            current: matchesRoutePattern(
              currentRoute,
              ADMIN_ROUTE_PATTERNS.routines
            ),
          },
        ],
      });
    }
    return nav;
  }

  const isCurrent = (id: SubNavigationAdminId): boolean =>
    matchesRoutePattern(currentRoute, ADMIN_ROUTE_PATTERNS[id]);

  const pathForSpaces = asPath ?? currentRoute;
  const isSystemSpaceCategoryCurrent = (category: string): boolean =>
    !!systemSpaceId &&
    pathForSpaces.includes(`/spaces/${systemSpaceId}/categories/${category}`);

  const hasAdminRole = isAdmin(owner);
  const hasManagerRole = isManager(owner);

  nav.push({
    id: "workspace",
    label: "Admin settings",
    menus: [
      ...(subscription.plan.limits.canUseProduct
        ? [
            {
              id: "personal_settings" as const,
              label: "Personal Settings",
              icon: User01,
              href: `/w/${owner.sId}/me`,
              current: isCurrent("personal_settings"),
            },
            {
              id: "my_wiki" as const,
              label: "My Wiki",
              icon: BookOpen01,
              href: `/w/${owner.sId}/me/wiki`,
              current: isCurrent("my_wiki"),
            },
            {
              id: "routines" as const,
              label: "Routines",
              icon: ClockRefresh,
              href: `/w/${owner.sId}/routines`,
              current: isCurrent("routines"),
            },
          ]
        : []),
      {
        id: "members",
        label: "People",
        icon: Users01,
        href: `/w/${owner.sId}/members`,
        current: isCurrent("members"),
        disabled: !hasManagerRole,
      },
      {
        id: "agents",
        label: "Agents",
        icon: Robot,
        href: `/w/${owner.sId}/builder/agents`,
        current: isCurrent("agents"),
        disabled: !hasManagerRole,
      },
      {
        id: "skills",
        label: "Skills",
        icon: PuzzlePiece01,
        href: `/w/${owner.sId}/builder/skills`,
        current: isCurrent("skills"),
        disabled: !hasManagerRole,
      },
      {
        id: "governance",
        label: "Settings & Governance",
        icon: Toggle01Left,
        href: `/w/${owner.sId}/governance`,
        current: isCurrent("governance"),
        disabled: !hasManagerRole,
      },
      ...(isCreditPricedPlan(subscription.plan) ||
      featureFlags.includes("usage_page_read_only")
        ? [
            {
              id: "usage" as const,
              label: "Usage",
              icon: PieChart01,
              href: `/w/${owner.sId}/usage`,
              current: isCurrent("usage"),
              disabled: !hasManagerRole,
            },
          ]
        : []),
      {
        id: "model_providers",
        label: "Model Providers",
        icon: Brain,
        href: `/w/${owner.sId}/model-providers`,
        current: isCurrent("model_providers"),
        disabled: !hasAdminRole,
      },
      {
        id: "analytics",
        label: "Analytics",
        icon: BarChart01,
        href: `/w/${owner.sId}/analytics`,
        current: isCurrent("analytics"),
        disabled: !hasManagerRole,
      },
      isCreditPricedPlan(subscription.plan)
        ? {
            id: "billing",
            label: "Billing",
            icon: CreditCard01,
            href: `/w/${owner.sId}/billing`,
            current: isCurrent("billing"),
            disabled: !canAdminBilling,
          }
        : {
            id: "subscription",
            label: "Subscription",
            icon: CreditCard01,
            href: `/w/${owner.sId}/subscription`,
            current: isCurrent("subscription"),
            disabled: !canAdminBilling,
          },
    ],
  });

  // Temporarily hidden from Admin sidebar (routes still exist if navigated directly).
  const showApiAndBuilderAdminSections = false;
  if (showApiAndBuilderAdminSections) {
    nav.push({
      id: "api",
      label: "API & Programmatic",
      menus: [
        {
          id: "api_keys",
          label: "API Keys",
          icon: Lock01,
          href: `/w/${owner.sId}/developers/api-keys`,
          current: isCurrent("api_keys"),
          disabled: !hasAdminRole,
        },
        ...(isCreditPricedPlan(subscription.plan)
          ? []
          : [
              {
                id: "credits_usage" as const,
                label: "Programmatic Usage",
                icon: Zap,
                href: `/w/${owner.sId}/developers/credits-usage`,
                current: isCurrent("credits_usage"),
                disabled: !hasAdminRole,
              },
            ]),
      ],
    });

    nav.push({
      id: "developers",
      label: "Builder Tools",
      menus: [
        {
          id: "providers",
          label: "App Credentials",
          icon: Shapes,
          href: `/w/${owner.sId}/developers/providers`,
          current: isCurrent("providers"),
          featureFlag: "legacy_dust_apps",
          disabled: !hasAdminRole,
        },
        {
          id: "dev_secrets",
          label: "Secrets",
          icon: Brackets,
          href: `/w/${owner.sId}/developers/dev-secrets`,
          current: isCurrent("dev_secrets"),
          disabled: !hasAdminRole,
        },
        {
          id: "sandbox",
          label: "Computer",
          icon: Globe01,
          href: `/w/${owner.sId}/developers/sandbox`,
          current: isCurrent("sandbox"),
          disabled: !hasAdminRole || !isComputerFeatureEnabled(featureFlags),
        },
        ...(computeIsSelfImprovementAvailable({
          owner,
          plan: subscription.plan,
          featureFlags,
        })
          ? [
              {
                id: "self_improving_skills" as const,
                label: "Self-Improving Skills",
                icon: Stars02,
                href: `/w/${owner.sId}/developers/self-improving-skills`,
                current: isCurrent("self_improving_skills"),
                disabled: !hasAdminRole,
              },
            ]
          : []),
      ],
    });
  }

  if (systemSpaceId) {
    nav.push({
      id: "spaces",
      label: "Spaces",
      menus: [
        {
          id: "spaces_connections",
          label: "Connections",
          icon: CloudArrowLeftRight,
          href: `/w/${owner.sId}/spaces/${systemSpaceId}/categories/managed`,
          current: isSystemSpaceCategoryCurrent("managed"),
          disabled: !hasAdminRole,
        },
        {
          id: "spaces_tools",
          label: "Tools",
          icon: ShapesPlus,
          href: `/w/${owner.sId}/spaces/${systemSpaceId}/categories/actions`,
          current: isSystemSpaceCategoryCurrent("actions"),
          disabled: !hasAdminRole,
        },
        {
          id: "spaces_triggers",
          label: "Triggers",
          icon: Zap,
          href: `/w/${owner.sId}/spaces/${systemSpaceId}/categories/triggers`,
          current: isSystemSpaceCategoryCurrent("triggers"),
          disabled: !hasAdminRole,
        },
      ],
    });
  }

  return nav;
};

export const subNavigationApp = ({
  owner,
  app,
  current,
  canAdministrateApps,
}: {
  owner: WorkspaceType;
  app: AppType;
  current: SubNavigationAppId;
  canAdministrateApps: boolean;
}) => {
  let nav = [
    {
      value: "specification",
      label: "Specification",
      icon: Terminal,
      href: `/w/${owner.sId}/spaces/${app.space.sId}/apps/${app.sId}`,
      current: current === "specification",
    },
    {
      value: "datasets",
      label: "Datasets",
      icon: File04,
      href: `/w/${owner.sId}/spaces/${app.space.sId}/apps/${app.sId}/datasets`,
      current: current === "datasets",
    },
  ];

  if (canAdministrateApps) {
    nav = nav.concat([
      {
        value: "runs",
        label: "Logs",
        icon: FolderOpen,
        href: `/w/${owner.sId}/spaces/${app.space.sId}/apps/${app.sId}/runs`,
        current: current === "runs",
      },
      {
        value: "settings",
        label: "Settings",
        icon: Settings01,
        href: `/w/${owner.sId}/spaces/${app.space.sId}/apps/${app.sId}/settings`,
        current: current === "settings",
      },
    ]);
  }

  return nav;
};
